/**
 * **Dangling references** — every slot of a recipe resolved against the board it lives on
 * (`specs/M6-A1-automation-engine.md` §5.1/§4.4, round 9l).
 *
 * `revalidate` (api) already parks an automation whose **column** or **group** is gone, because
 * those are the two repairs monday itself performs: of the 405 `legacy_automations` rows on this
 * account, 13 are `auto_disabled_by_deleted_resource`, 1 is `auto_disabled_by_column` and the
 * notices are `missing_column` (9) / `missing_group` (8). monday has no such repair for a
 * deleted **label**, and board *Activities* proves the consequence: automation `64659153` sets
 * `status` to `{index: 1}` while that column's labels are `{3: Open, 4: Done}` — label `1` was
 * deleted after the automation was written, and monday still reports the row `state: active`,
 * `noticeMessage: null`, `invalid: false`.
 *
 * Two things follow, and this file is both of them:
 *
 * 1. **The value is a label id, not a position.** Across the snapshot 103 of the 104 label
 *    references resolve as an id (33 of them are *not* a valid position), so reading them as
 *    positions would break 33 working automations to rescue one. Nothing is remapped here; a
 *    label monday cannot resolve is reported, never guessed (CLAUDE.md rule 3).
 * 2. **Nothing is ever switched off by this module.** It answers *what is broken*; the centre
 *    shows `notice_message` and the run history says why (M6-A1 §8.1, §5.4).
 *
 * Labels are resolved through the **column type registry** (`bucketVocabulary`), so no caller
 * branches on `status` or `dropdown` outside their folders (CLAUDE.md rule 6), and which
 * parameters *are* labels comes from `slots.ts`, the same table the builder's pickers use.
 */
import { findColumnType } from '../columns/index.js';
import { automationBlockRegistry } from './block.js';
import { slotsOf } from './slots.js';
import { isAnyValue } from './values.js';
/* ── labels ───────────────────────────────────────────────────────────────── */
/**
 * The label ids a column can hold, or `null` when the type has no closed vocabulary (and a
 * label reference against it therefore cannot be judged).
 *
 * `bucketVocabulary` is the registry's own declaration of "the column's whole vocabulary, in its
 * own order" — the list the quick-filter pane and group-by render. Deactivated options are added
 * back: `dropdown` hides them from the vocabulary, but a deactivated option still **exists**, so
 * an automation pointing at one is configured, not broken.
 */
export function columnLabelIds(column) {
    const definition = findColumnType(column.type);
    if (definition?.bucketVocabulary === undefined)
        return null;
    let settings = column.settings;
    const parsed = definition.settingsSchema.safeParse(column.settings);
    if (parsed.success)
        settings = parsed.data;
    else {
        // A column whose settings never went through the normaliser carries monday's array form.
        try {
            settings = definition.fromMondaySettings(column.settings);
        }
        catch {
            return null;
        }
    }
    const ids = new Set();
    for (const bucket of definition.bucketVocabulary(settings)) {
        const id = Number(bucket.key);
        if (Number.isInteger(id))
            ids.add(id);
    }
    const deactivated = settings.deactivated_labels;
    if (Array.isArray(deactivated)) {
        for (const entry of deactivated) {
            const id = Number(entry);
            if (Number.isInteger(id))
                ids.add(id);
        }
    }
    return ids;
}
/** Does this column still hold that label id? `null` ⇒ the question does not apply. */
export function columnHasLabel(column, labelId) {
    const ids = columnLabelIds(column);
    if (ids === null)
        return null;
    // A column with no labels at all accepts anything on write (both types gate on
    // `labels.length > 0`), so it cannot make a reference dangle either.
    if (ids.size === 0)
        return true;
    return ids.has(labelId);
}
/** The label ids a slot value holds. `$any$` is a choice, not a reference, so it yields none. */
function labelIdsOfValue(value) {
    if (value === undefined || value === null || isAnyValue(value))
        return [];
    if (Array.isArray(value))
        return value.flatMap((entry) => labelIdsOfValue(entry));
    if (typeof value === 'number')
        return Number.isInteger(value) ? [value] : [];
    if (typeof value === 'string')
        return /^-?\d+$/.test(value.trim()) ? [Number(value.trim())] : [];
    if (typeof value === 'object') {
        const index = value.index;
        if (typeof index === 'number' && Number.isInteger(index))
            return [index];
        if (typeof index === 'string' && /^-?\d+$/.test(index.trim()))
            return [Number(index.trim())];
        const ids = value.ids;
        if (Array.isArray(ids))
            return labelIdsOfValue(ids);
    }
    return [];
}
/** Which parameters of a block are label pickers (`slots.ts` is the one table). */
const LABEL_SLOTS = new Set(['status_label', 'dropdown_labels']);
function labelParamsOf(block) {
    return slotsOf(block)
        .filter(({ slot }) => LABEL_SLOTS.has(slot.kind))
        .map(({ param }) => param);
}
function prepare(node) {
    const block = automationBlockRegistry.find(node.block);
    if (block === undefined)
        return null;
    const parsed = block.paramsSchema.safeParse(node.params);
    // An unparseable node is `validateRecipe`'s business (`invalid_params`), not the linter's.
    if (!parsed.success)
        return null;
    return {
        nodeId: node.nodeId,
        block,
        params: parsed.data,
        references: block.references(parsed.data),
    };
}
/**
 * Column ids this node points at **somewhere else**: on another board (the cross-board actions
 * of M6-A4) or on the board's subitems board, which monday's own column picker offers
 * (`slots.ts`, capture `35-date-column-picker`). `references()` carries only the id, so the
 * shape of the parameter is where that lives — and a reference this board cannot own must not
 * be reported as missing from it, or a perfectly good automation gets a notice.
 */
function foreignColumnIds(params, boardId) {
    const out = new Set();
    const seen = new Set();
    const visit = (value) => {
        if (Array.isArray(value)) {
            for (const entry of value)
                visit(entry);
            return;
        }
        if (typeof value !== 'object' || value === null)
            return;
        if (seen.has(value))
            return;
        seen.add(value);
        const ref = value;
        if (typeof ref.columnId === 'string') {
            const elsewhere = ref.boardId !== undefined && ref.boardId !== null && String(ref.boardId) !== boardId;
            if (elsewhere || ref.isSubitemColumn === true)
                out.add(ref.columnId);
        }
        for (const entry of Object.values(value))
            visit(entry);
    };
    visit(params);
    return out;
}
/** The single column a node points at, for resolving its label slots. */
function columnOf(node) {
    const columns = node.references.filter((ref) => ref.kind === 'column');
    return columns.length === 1 ? columns[0]?.id : undefined;
}
/**
 * Every dangling reference of one recipe, in node order.
 *
 * Blocks `45` and `48` carry a label but **no column of their own** — their inbound wiring reads
 * the trigger's `node_results` (`specs/M6-A1` §5.3) — so their labels are resolved against the
 * trigger's column. A label whose column cannot be determined is not reported at all.
 */
export function lintRecipe(recipe, shape) {
    const nodes = [recipe.trigger, ...recipe.conditions, ...recipe.actions]
        .map(prepare)
        .filter((node) => node !== null);
    const triggerColumn = nodes[0] === undefined ? undefined : columnOf(nodes[0]);
    const out = [];
    for (const node of nodes) {
        const { nodeId, block } = node;
        const missingColumns = new Set();
        const foreign = foreignColumnIds(node.params, shape.boardId);
        for (const ref of node.references) {
            const base = { nodeId, block: block.id, kind: ref.kind, id: ref.id };
            if (ref.kind === 'column' && foreign.has(ref.id))
                continue;
            if (ref.invalid === true) {
                out.push({ ...base, reason: 'marked_invalid', detail: `${ref.kind} ${ref.id} is marked invalid by monday` });
                continue;
            }
            if (ref.kind === 'column') {
                const column = shape.columns.get(ref.id);
                if (column === undefined) {
                    missingColumns.add(ref.id);
                    out.push({ ...base, reason: 'missing', detail: `column ${ref.id} does not exist on board ${shape.boardId}` });
                    continue;
                }
                const wanted = block.requiredColumnTypes;
                if (wanted !== undefined && wanted.length > 0 && !wanted.includes(column.type)) {
                    out.push({
                        ...base,
                        reason: 'wrong_type',
                        detail: `column ${ref.id} is a ${column.type}, block ${block.id} needs ${wanted.join('/')}`,
                    });
                }
            }
            else if (ref.kind === 'group') {
                if (!shape.groups.has(ref.id)) {
                    out.push({ ...base, reason: 'missing', detail: `group ${ref.id} does not exist on board ${shape.boardId}` });
                }
            }
            else if (ref.kind === 'board') {
                if (shape.boards !== undefined && !shape.boards.has(ref.id)) {
                    out.push({ ...base, reason: 'missing', detail: `board ${ref.id} does not exist` });
                }
            }
            else if (ref.kind === 'user') {
                if (shape.users !== undefined && !shape.users.has(ref.id)) {
                    out.push({ ...base, reason: 'missing', detail: `user ${ref.id} does not exist or is deactivated` });
                }
            }
            else if (ref.kind === 'team') {
                if (shape.teams !== undefined && !shape.teams.has(ref.id)) {
                    out.push({ ...base, reason: 'missing', detail: `team ${ref.id} does not exist` });
                }
            }
            // `app` references are the connections page's business (`state: not_connected`), not this.
        }
        const columnId = columnOf(node) ?? triggerColumn;
        if (columnId === undefined || missingColumns.has(columnId) || foreign.has(columnId))
            continue;
        const column = shape.columns.get(columnId);
        if (column === undefined)
            continue;
        for (const param of labelParamsOf(block)) {
            for (const labelId of labelIdsOfValue(node.params[param])) {
                if (columnHasLabel(column, labelId) === false) {
                    out.push({
                        nodeId,
                        block: block.id,
                        kind: 'label',
                        id: String(labelId),
                        columnId,
                        param,
                        reason: 'missing',
                        detail: `label ${labelId} does not exist in column ${columnId} of board ${shape.boardId}`,
                    });
                }
            }
        }
    }
    return out;
}
/* ── the runtime side ─────────────────────────────────────────────────────── */
/**
 * An action asked to use a reference the board no longer has. Its own error class, next to
 * `AutomationNotConnected` / `AutomationNotConfigured`, because the engine must treat it the
 * same way: record *why* on the run and leave the automation switched on (brief 9l — never
 * auto-disable). Counting it as a failure would park the automation as `error` after
 * `AUTOMATION_FAILURE_THRESHOLD` ticks, which is exactly the auto-disable we must not do.
 */
export class AutomationDanglingReference extends Error {
    kind;
    id;
    code = 'dangling_reference';
    constructor(kind, id, detail) {
        super(detail);
        this.kind = kind;
        this.id = id;
        this.name = 'AutomationDanglingReference';
    }
}
export const isDanglingReference = (error) => error instanceof AutomationDanglingReference ||
    (typeof error === 'object' && error !== null && error.code === 'dangling_reference');
/**
 * The runtime check an action runs before it writes a label: same question as the linter's,
 * asked against the column the engine just read. `settings` is the stored canonical settings.
 */
export function assertLabelExists(column, labelId, blockId) {
    if (columnHasLabel(column, labelId) === false) {
        throw new AutomationDanglingReference('label', String(labelId), `block ${blockId}: label ${labelId} does not exist in column ${column.id} — it was deleted on monday after this automation was configured`);
    }
}
//# sourceMappingURL=lint.js.map